<script lang="ts">
  import Readout from '../../lib/components/charts/Readout.svelte';
  import type { TimelineDay } from '../../lib/api/types';
  import { locale, t } from '../../lib/i18n/index.ts';
  import { categoryColors, categoryLabel, span } from './format';

  // DayRing is how the member's own day split between places by category,
  // travel, and hours the timeline has no record of (spec 005 FR-011),
  // up to now for today. The unrecorded share is drawn hollow, so it reads
  // apart without its color. As every chart does (spec 010 FR-033),
  // hovering a share or moving with the arrow keys reads it above the ring,
  // and the readout lists every share with its time.
  let { day }: { day: TimelineDay } = $props();

  const SIZE = 104;
  const STROKE = 14;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  // GAP is the surface gap between shares, in pixels along the ring.
  const GAP = 2;

  type Share = { label: string; seconds: number; color: string; hollow?: boolean };
  const shares = $derived.by(() => {
    const out: Share[] = (day.categories ?? []).filter((c) => c.seconds > 0)
      .map((c) => ({ label: categoryLabel(c.category), seconds: c.seconds, color: categoryColors[c.category] }));
    if (day.travel_s) out.push({ label: t('timelineTraveling'), seconds: day.travel_s, color: 'var(--theme-sky)' });
    if (day.unrecorded_s) out.push({ label: t('timelineNotRecorded'), seconds: day.unrecorded_s, color: 'var(--theme-overlay2)', hollow: true });
    return out;
  });
  const total = $derived(shares.reduce((a, s) => a + s.seconds, 0));
  // arcs place each share along the ring from the top, clockwise.
  const arcs = $derived.by(() => {
    let at = 0;
    return shares.map((s) => {
      const len = total ? (s.seconds / total) * C : 0;
      const arc = { ...s, start: at, len };
      at += len;
      return arc;
    });
  });

  let hover = $state<number | null>(null);
  let at = $state<number | null>(null);
  const shown = $derived(hover ?? at);
  const pct = (s: number) => new Intl.NumberFormat(locale(), { style: 'percent', maximumFractionDigits: 0 }).format(s / total);
  const reading = $derived(shown != null ? shares[shown] : null);
  const readingText = $derived(reading ? t('timelineShareReading', { label: reading.label, time: span(reading.seconds), percent: pct(reading.seconds) }) : '');

  function key(e: KeyboardEvent) {
    const n = shares.length;
    if (!n) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') at = at == null ? 0 : (at + 1) % n;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') at = at == null ? n - 1 : (at - 1 + n) % n;
    else if (e.key === 'Escape') at = null;
    else return;
    hover = null;
    e.preventDefault();
  }
</script>

{#if total > 0}
  <div class="flex items-start gap-4">
    <div class="shrink-0 rounded-full outline-none focus-visible:ring-2" style="--tw-ring-color: var(--theme-focus-ring-color);"
      role="slider" tabindex="0" aria-label={t('timelineRingName')} aria-valuemin={0} aria-valuemax={Math.max(0, shares.length - 1)} aria-valuenow={shown ?? 0}
      aria-valuetext={reading ? readingText : t('timelineNoSharePicked')}
      onkeydown={key} onmouseleave={() => (hover = null)}>
      <svg width={SIZE} height={SIZE} viewBox="0 0 {SIZE} {SIZE}" aria-hidden="true">
        <g transform="rotate(-90 {SIZE / 2} {SIZE / 2})">
          {#each arcs as a, i (a.label)}
            {@const len = Math.max(0.5, a.len - (arcs.length > 1 ? GAP : 0))}
            {@const dim = shown != null && shown !== i}
            {#if a.hollow}
              {#each [R - STROKE / 2 + 1, R + STROKE / 2 - 1] as rr (rr)}
                <circle cx={SIZE / 2} cy={SIZE / 2} r={rr} fill="none" stroke={a.color} stroke-width="1.5" opacity={dim ? 0.35 : 1}
                  stroke-dasharray="{(len * rr) / R} {2 * Math.PI * rr}" stroke-dashoffset={(-a.start * rr) / R} />
              {/each}
            {:else}
              <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke={a.color} stroke-width={STROKE} opacity={dim ? 0.35 : 1}
                stroke-dasharray="{len} {C}" stroke-dashoffset={-a.start} />
            {/if}
            <!-- A wide transparent stroke over each share is its hover target. -->
            <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="transparent" stroke-width={STROKE + 6} role="presentation"
              stroke-dasharray="{len} {C}" stroke-dashoffset={-a.start} style="pointer-events: stroke;" onmouseenter={() => (hover = i)} onclick={() => (at = i)} />
          {/each}
        </g>
      </svg>
    </div>
    <div class="flex-1 min-w-0">
      <Readout title={reading ? readingText : t('timelineDayWent', { time: span(total) })}
        items={shares.map((s) => ({ label: s.label, value: span(s.seconds), color: s.color, mark: s.hollow ? ('hollow' as const) : ('bar' as const) }))} />
    </div>
  </div>
{/if}
